import { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { API, authFetch } from "@/App";
import PageLayout, { PageHeader } from "@/components/PageLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { toast } from "sonner";
import { APIProvider, Map, AdvancedMarker, useMap } from "@vis.gl/react-google-maps";
import { 
  Search, Star, MapPin, Phone, Mail, ChevronRight, List, Map as MapIcon,
  ShoppingBag, Stethoscope, Scissors, Home,
  CheckCircle, Clock, Award, Calendar, Navigation, X,
  AlertCircle, ArrowLeft
} from "lucide-react";
import BookingDialog from "@/components/BookingDialog";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

// Default center (Rome, Italy)
const DEFAULT_CENTER = { lat: 41.9028, lng: 12.4964 };

// Service categories - ONLY physical locations on map (NO dog sitter, educatori)
const categories = [
  { id: "veterinario", label: "Veterinari", icon: Stethoscope, color: "from-blue-500 to-indigo-600", lightBg: "bg-blue-50", textColor: "text-blue-600", markerColor: "#3B82F6" },
  { id: "clinica", label: "Cliniche", icon: Stethoscope, color: "from-indigo-500 to-purple-600", lightBg: "bg-indigo-50", textColor: "text-indigo-600", markerColor: "#6366F1" },
  { id: "toelettatura", label: "Toelettatura", icon: Scissors, color: "from-pink-500 to-rose-600", lightBg: "bg-pink-50", textColor: "text-pink-600", markerColor: "#EC4899" },
  { id: "negozio", label: "Pet Shop", icon: ShoppingBag, color: "from-emerald-500 to-teal-600", lightBg: "bg-emerald-50", textColor: "text-emerald-600", markerColor: "#10B981" },
  { id: "pensione", label: "Pensioni", icon: Home, color: "from-amber-500 to-orange-600", lightBg: "bg-amber-50", textColor: "text-amber-600", markerColor: "#F59E0B" },
];

const professionConfig = {
  veterinario: { icon: Stethoscope, color: "text-blue-600", bg: "bg-blue-50", markerColor: "#3B82F6" },
  clinica: { icon: Stethoscope, color: "text-indigo-600", bg: "bg-indigo-50", markerColor: "#6366F1" },
  toelettatura: { icon: Scissors, color: "text-pink-600", bg: "bg-pink-50", markerColor: "#EC4899" },
  negozio: { icon: ShoppingBag, color: "text-emerald-600", bg: "bg-emerald-50", markerColor: "#10B981" },
  pensione: { icon: Home, color: "text-amber-600", bg: "bg-amber-50", markerColor: "#F59E0B" },
};

// Sample data with coordinates - ONLY physical locations (no dog sitter/educatori)
const sampleLocations = [
  { professional_id: "1", name: "Clinica Veterinaria Roma Nord", profession_type: "veterinario", city: "Roma", address: "Via Flaminia 123", rating: 4.8, reviews_count: 124, lat: 41.9341, lng: 12.4656, verified: true, description: "Clinica veterinaria con servizio 24h. Specializzati in chirurgia e medicina interna.", phone: "+39 06 1234567" },
  { professional_id: "3", name: "Clinica Veterinaria Trastevere", profession_type: "veterinario", city: "Roma", address: "Viale Trastevere 89", rating: 4.9, reviews_count: 201, lat: 41.8879, lng: 12.4694, verified: true, description: "Emergenze 24/7, diagnostica per immagini, laboratorio analisi.", phone: "+39 06 9876543" },
  { professional_id: "5", name: "Toelettatura Bella Zampa", profession_type: "toelettatura", city: "Roma", address: "Via Appia Nuova 234", rating: 4.7, reviews_count: 56, lat: 41.8719, lng: 12.5113, verified: false, description: "Toelettatura professionale, bagno, taglio, stripping. Spa per animali." },
  { professional_id: "6", name: "Pet Shop Animalia", profession_type: "negozio", city: "Roma", address: "Via Tiburtina 567", rating: 4.5, reviews_count: 78, lat: 41.9100, lng: 12.5200, verified: true, description: "Cibo premium, accessori, giochi, cucce. Consegna a domicilio disponibile." },
  { professional_id: "7", name: "Pensione Amici Pelosi", profession_type: "pensione", city: "Roma", address: "Via Cassia km 12", rating: 4.6, reviews_count: 112, lat: 42.0100, lng: 12.4500, verified: true, description: "Pensione per cani e gatti con ampi spazi verdi. Box singoli climatizzati.", phone: "+39 06 3334567" },
  { professional_id: "9", name: "Clinica San Francesco", profession_type: "clinica", city: "Roma", address: "Via Nomentana 45", rating: 4.7, reviews_count: 89, lat: 41.9200, lng: 12.5050, verified: true, description: "Centro veterinario specializzato in ortopedia e neurologia.", phone: "+39 06 4445566" },
];

export default function Marketplace() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState(searchParams.get('filter') || "all");
  const [selectedPro, setSelectedPro] = useState(null);
  const [showBooking, setShowBooking] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [viewMode, setViewMode] = useState("map"); // "map" or "list"
  const [userLocation, setUserLocation] = useState(null);
  const [mapCenter, setMapCenter] = useState(DEFAULT_CENTER);
  const [sortBy, setSortBy] = useState("distance"); // "distance" or "rating"

  useEffect(() => {
    setIsMobile(window.innerWidth < 1024);
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);
    fetchProfessionals();
    getUserLocation();
    
    // Read filter from URL params
    const filterParam = searchParams.get('filter');
    if (filterParam && categories.some(c => c.id === filterParam)) {
      setFilterType(filterParam);
    }
    
    return () => window.removeEventListener('resize', handleResize);
  }, [searchParams]);

  useEffect(() => {
    fetchProfessionals();
  }, [filterType]);

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setUserLocation(loc);
          setMapCenter(loc);
        },
        (error) => {
          console.log("Geolocation error:", error.code, error.message);
          // Show user-friendly message based on error type
          if (error.code === 1) {
            // Permission denied - user can still use the app with default location
            console.log("Geolocation permission denied - using default location");
          } else if (error.code === 2) {
            console.log("Position unavailable - using default location");
          } else if (error.code === 3) {
            console.log("Geolocation timeout - using default location");
          }
          // Keep default center
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000 // 5 minutes cache
        }
      );
    } else {
      console.log("Geolocation not supported");
    }
  };

  const fetchProfessionals = async () => {
    try {
      let url = `${API}/marketplace/professionals`;
      if (filterType !== "all") url += `?profession_type=${filterType}`;
      
      const response = await authFetch(url);
      if (response.ok) {
        const data = await response.json();
        // Add sample coordinates if not present (for demo)
        const withCoords = data.map((pro, index) => {
          if (!pro.lat || !pro.lng) {
            // Assign coordinates from sample data or generate random near Rome
            const sample = sampleLocations[index % sampleLocations.length];
            return {
              ...pro,
              lat: sample?.lat || DEFAULT_CENTER.lat + (Math.random() - 0.5) * 0.1,
              lng: sample?.lng || DEFAULT_CENTER.lng + (Math.random() - 0.5) * 0.1
            };
          }
          return pro;
        });
        setProfessionals(withCoords.length > 0 ? withCoords : sampleLocations);
      } else {
        // Use sample data if API fails
        setProfessionals(sampleLocations);
      }
    } catch (error) {
      console.error("Error fetching professionals:", error);
      setProfessionals(sampleLocations);
    } finally {
      setLoading(false);
    }
  };

  const filteredProfessionals = useMemo(() => {
    // First, filter out home services (dog_sitter, cinofilo) from marketplace
    const physicalLocationsOnly = professionals.filter(pro => 
      !['dog_sitter', 'cinofilo'].includes(pro.profession_type)
    );
    
    return physicalLocationsOnly.filter(pro => {
      const matchesSearch = 
        pro.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pro.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        pro.city?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesFilter = filterType === "all" || pro.profession_type === filterType;
      return matchesSearch && matchesFilter;
    });
  }, [professionals, searchQuery, filterType]);

  // Calculate distance between two points (Haversine formula)
  const calculateDistance = (from, to) => {
    if (!from || !to?.lat || !to?.lng) return null;
    
    const R = 6371; // Earth's radius in km
    const dLat = (to.lat - from.lat) * Math.PI / 180;
    const dLng = (to.lng - from.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(from.lat * Math.PI / 180) * Math.cos(to.lat * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;
    
    if (distance < 1) {
      return `${Math.round(distance * 1000)} m`;
    }
    return `${distance.toFixed(1)} km`;
  };

  // Get numeric distance for sorting
  const getDistanceValue = (pro) => {
    if (!userLocation || !pro?.lat || !pro?.lng) return Infinity;
    
    const R = 6371;
    const dLat = (pro.lat - userLocation.lat) * Math.PI / 180;
    const dLng = (pro.lng - userLocation.lng) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(userLocation.lat * Math.PI / 180) * Math.cos(pro.lat * Math.PI / 180) * 
      Math.sin(dLng/2) * Math.sin(dLng/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  // Sorted professionals based on sortBy
  const sortedProfessionals = useMemo(() => {
    const sorted = [...filteredProfessionals];
    if (sortBy === "distance") {
      sorted.sort((a, b) => getDistanceValue(a) - getDistanceValue(b));
    } else if (sortBy === "rating") {
      sorted.sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    return sorted;
  }, [filteredProfessionals, sortBy, userLocation]);

  // Open location in native maps app
  const openInMaps = (pro) => {
    if (!pro?.lat || !pro?.lng) {
      toast.error("Posizione non disponibile");
      return;
    }
    
    const destination = `${pro.lat},${pro.lng}`;
    const label = encodeURIComponent(pro.name);
    
    // Try to detect platform and open appropriate map
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isAndroid = /Android/.test(navigator.userAgent);
    
    let url;
    if (isIOS) {
      // Apple Maps
      url = `maps://maps.apple.com/?daddr=${destination}&q=${label}`;
    } else if (isAndroid) {
      // Google Maps app
      url = `geo:${destination}?q=${destination}(${label})`;
    } else {
      // Fallback to Google Maps web
      url = `https://www.google.com/maps/dir/?api=1&destination=${destination}&destination_place_id=${label}`;
    }
    
    window.open(url, '_blank');
  };

  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <Star key={i} size={14} className={i < Math.floor(rating) ? "fill-amber-400 text-amber-400" : "text-gray-200"} />
    ));
  };

  const centerOnUser = () => {
    if (userLocation) {
      setMapCenter(userLocation);
    } else {
      getUserLocation();
      toast.info("Ottenendo la tua posizione...");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
        <div className="w-12 h-12 rounded-full border-2 border-[#FF6B4A] border-t-transparent animate-spin" />
      </div>
    );
  }

  // Check if API key is configured
  const hasApiKey = GOOGLE_MAPS_API_KEY && GOOGLE_MAPS_API_KEY.length > 10;

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="marketplace-page">
      {/* Desktop Layout */}
      {!isMobile ? (
        <div className="min-h-screen flex flex-col">
          {/* Header - Standard */}
          <div className="bg-white border-b border-black/5 px-6 py-4 flex-shrink-0">
            <div className="max-w-6xl mx-auto">
              <PageHeader 
                title="Mappa"
                subtitle="Veterinari, cliniche e negozi vicino a te"
                icon={MapIcon}
                iconColor="#10B981"
              />

              {/* Search & Filters */}
              <div className="flex gap-4 -mt-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
                  <Input
                    type="text"
                    placeholder="Cerca per nome, città o servizio..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-10 pl-10 rounded-xl bg-[#F5F5F7] border-0"
                  />
                </div>
              </div>

              {/* Category Pills */}
              <div className="flex gap-2 mt-3 flex-wrap pb-1">
                <button
                  onClick={() => setFilterType("all")}
                  className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                    filterType === "all" ? 'bg-[#1D1D1F] text-white' : 'bg-[#F5F5F7] text-[#6E6E73] hover:bg-[#EBEBEB]'
                  }`}
                >
                  Tutti ({professionals.filter(p => !['dog_sitter', 'cinofilo'].includes(p.profession_type)).length})
                </button>
                {categories.map((cat) => {
                  const count = professionals.filter(p => p.profession_type === cat.id).length;
                  return (
                    <button
                      key={cat.id}
                      onClick={() => setFilterType(cat.id)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap flex items-center gap-2 transition-all ${
                        filterType === cat.id ? 'bg-[#1D1D1F] text-white' : 'bg-[#F5F5F7] text-[#6E6E73] hover:bg-[#EBEBEB]'
                      }`}
                    >
                      <cat.icon size={16} />
                      {cat.label} ({count})
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Content - Split View: Map + List */}
          <div className="flex gap-6 p-6 max-w-6xl mx-auto w-full" style={{ height: 'calc(100vh - 200px)', maxHeight: '700px' }}>
            {/* Map Section - Left Side with rounded corners */}
            <div className="flex-1 relative bg-white rounded-3xl overflow-hidden shadow-sm border border-black/5">
              {hasApiKey ? (
                <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
                  <Map
                    style={{ width: "100%", height: "100%" }}
                    defaultCenter={mapCenter}
                    defaultZoom={13}
                    gestureHandling="greedy"
                    disableDefaultUI={true}
                    zoomControl={true}
                    mapTypeControl={false}
                    streetViewControl={false}
                    fullscreenControl={false}
                  >
                    <MapMarkers 
                      professionals={filteredProfessionals} 
                      onSelect={setSelectedPro}
                      userLocation={userLocation}
                    />
                  </Map>
                </APIProvider>
              ) : (
                <MapPlaceholder onCenterUser={centerOnUser} filteredCount={filteredProfessionals.length} />
              )}

              {/* Floating Controls */}
              <div className="absolute bottom-4 right-4 flex flex-col gap-2">
                <Button
                  onClick={centerOnUser}
                  className="w-11 h-11 rounded-xl bg-white shadow-lg hover:bg-gray-50"
                  variant="ghost"
                >
                  <Navigation size={18} className="text-[#FF6B4A]" />
                </Button>
              </div>

              {/* Results Count Badge */}
              <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-xl px-3 py-2 shadow-sm border border-black/5">
                <p className="text-sm font-medium text-[#1D1D1F]">
                  {filteredProfessionals.length} servizi
                </p>
              </div>
            </div>

            {/* List Section - Right Side */}
            <div className="w-[380px] bg-white rounded-3xl shadow-sm border border-black/5 overflow-hidden flex flex-col">
              <div className="p-4 border-b border-black/5">
                <h3 className="font-bold text-[#1D1D1F]">Risultati</h3>
              </div>
              <div className="flex-1 overflow-y-auto p-4">
                <div className="space-y-3">
                  {filteredProfessionals.length === 0 ? (
                    <div className="text-center py-8">
                      <Search className="mx-auto text-[#D1D1D6] mb-3" size={32} />
                      <p className="text-[#86868B] text-sm">Nessun risultato</p>
                    </div>
                  ) : (
                    filteredProfessionals.map((pro) => {
                      const config = professionConfig[pro.profession_type] || professionConfig.veterinario;
                      const Icon = config.icon;
                      
                      return (
                        <div
                          key={pro.professional_id}
                          className="bg-[#F5F5F7] rounded-2xl p-3 hover:bg-[#EBEBEB] transition-all cursor-pointer"
                          onClick={() => setSelectedPro(pro)}
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
                              <Icon size={18} className={config.color} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <h4 className="font-semibold text-[#1D1D1F] text-sm truncate">{pro.name}</h4>
                                {pro.verified && (
                                  <CheckCircle className="text-emerald-500 flex-shrink-0" size={14} />
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <div className="flex items-center gap-0.5">
                                  <Star size={12} className="fill-amber-400 text-amber-400" />
                                  <span className="text-xs text-[#6E6E73]">{pro.rating}</span>
                                </div>
                                <span className="text-xs text-[#86868B]">•</span>
                                <span className="text-xs text-[#86868B] flex items-center gap-1">
                                  <MapPin size={10} />{pro.city}
                                </span>
                              </div>
                            </div>
                            <ChevronRight className="text-[#C7C7CC]" size={18} />
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Mobile Layout - Lista Servizi SENZA mappa integrata */
        <div className="min-h-screen pb-32 bg-[#FAFAFA] overflow-x-hidden" data-testid="marketplace-page-mobile">
          {/* Header */}
          <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-black/5">
            <div className="px-4 py-3">
              <div className="flex items-center gap-3 mb-3">
                <button 
                  onClick={() => navigate(-1)} 
                  className="w-9 h-9 rounded-xl bg-[#F5F5F7] flex items-center justify-center"
                >
                  <ArrowLeft size={18} />
                </button>
                <h1 className="text-lg font-bold text-[#1D1D1F] flex-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  Servizi
                </h1>
              </div>

              {/* Location Input or GPS Status */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={centerOnUser}
                  className={`flex-1 h-10 rounded-xl flex items-center justify-center gap-2 transition-all ${
                    userLocation 
                      ? 'bg-emerald-50 text-emerald-600 border border-emerald-200' 
                      : 'bg-[#F5F5F7] text-[#6E6E73]'
                  }`}
                >
                  <Navigation size={16} />
                  <span className="text-sm font-medium">
                    {userLocation ? "Posizione attiva" : "Usa la mia posizione"}
                  </span>
                </button>
              </div>

              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#86868B]" size={18} />
                <Input
                  type="text"
                  placeholder="Cerca servizi..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-10 pl-10 rounded-xl bg-[#F5F5F7] border-0"
                />
              </div>

              {/* Sorting */}
              <div className="flex gap-2 mb-3">
                <button
                  onClick={() => setSortBy("distance")}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
                    sortBy === "distance" 
                      ? 'bg-[#1D1D1F] text-white' 
                      : 'bg-[#F5F5F7] text-[#6E6E73]'
                  }`}
                >
                  Più vicini
                </button>
                <button
                  onClick={() => setSortBy("rating")}
                  className={`flex-1 py-2 rounded-xl text-sm font-medium transition-all ${
                    sortBy === "rating" 
                      ? 'bg-[#1D1D1F] text-white' 
                      : 'bg-[#F5F5F7] text-[#6E6E73]'
                  }`}
                >
                  Migliori recensioni
                </button>
              </div>

              {/* Category Pills */}
              <div className="flex gap-2 flex-wrap pb-2">
                <button
                  onClick={() => setFilterType("all")}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap ${
                    filterType === "all" ? 'bg-[#1D1D1F] text-white' : 'bg-[#F5F5F7] text-[#6E6E73]'
                  }`}
                >
                  Tutti
                </button>
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setFilterType(cat.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap flex items-center gap-1.5 ${
                      filterType === cat.id ? 'bg-[#1D1D1F] text-white' : 'bg-[#F5F5F7] text-[#6E6E73]'
                    }`}
                  >
                    <cat.icon size={14} />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Services List */}
          <div className="px-4 py-3 space-y-3">
            {sortedProfessionals.length === 0 ? (
              <div className="text-center py-12">
                <MapPin className="text-[#D1D1D6] mx-auto mb-3" size={48} />
                <p className="text-[#86868B]">Nessun servizio trovato</p>
              </div>
            ) : (
              sortedProfessionals.map((pro) => {
                const config = professionConfig[pro.profession_type] || professionConfig.veterinario;
                const Icon = config.icon;
                const distance = calculateDistance(userLocation, pro);
                
                return (
                  <div
                    key={pro.professional_id}
                    className="bg-white rounded-2xl p-4 shadow-sm border border-black/5"
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-12 h-12 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
                        <Icon size={24} className={config.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <div>
                            <h3 className="font-semibold text-[#1D1D1F]">{pro.name}</h3>
                            <div className="flex items-center gap-1 mt-0.5">
                              {renderStars(pro.rating)}
                              <span className="text-xs text-[#86868B] ml-1">({pro.reviews_count})</span>
                            </div>
                          </div>
                          {distance && (
                            <span className="text-sm font-semibold text-[#FF6B4A] bg-[#FF6B4A]/10 px-2 py-1 rounded-lg">
                              {distance}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[#86868B] flex items-center gap-1 mt-1">
                          <MapPin size={12} />{pro.address || pro.city}
                        </p>
                        {pro.description && (
                          <p className="text-xs text-[#6E6E73] mt-2 line-clamp-2">{pro.description}</p>
                        )}
                      </div>
                    </div>
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => setSelectedPro(pro)}
                        className="flex-1 py-2.5 bg-[#F5F5F7] text-[#1D1D1F] rounded-xl text-sm font-medium hover:bg-[#EBEBEB] transition-colors"
                      >
                        Dettagli
                      </button>
                      <button
                        onClick={() => openInMaps(pro)}
                        className="flex-1 py-2.5 bg-[#FF6B4A] text-white rounded-xl text-sm font-medium flex items-center justify-center gap-2 hover:bg-[#E55A3A] transition-colors"
                      >
                        <Navigation size={16} />
                        Indicazioni
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* Professional Detail Dialog */}
      <ProfessionalDialog 
        professional={selectedPro}
        open={!!selectedPro && !showBooking}
        onClose={() => setSelectedPro(null)}
        onBook={() => setShowBooking(true)}
        renderStars={renderStars}
      />

      {/* Booking Dialog */}
      {selectedPro && showBooking && (
        <BookingDialog
          professional={selectedPro}
          onClose={() => { setShowBooking(false); setSelectedPro(null); }}
          onSuccess={() => {
            setShowBooking(false);
            setSelectedPro(null);
            toast.success("Prenotazione effettuata!");
            navigate('/bookings');
          }}
        />
      )}
    </div>
  );
}

// Map Markers Component (must be inside Map)
function MapMarkers({ professionals, onSelect, userLocation }) {
  const map = useMap();

  return (
    <>
      {/* User Location Marker */}
      {userLocation && (
        <AdvancedMarker position={userLocation}>
          <div className="relative">
            <div className="w-6 h-6 bg-blue-500 rounded-full border-3 border-white shadow-lg flex items-center justify-center">
              <div className="w-2 h-2 bg-white rounded-full" />
            </div>
            <div className="absolute inset-0 bg-blue-500 rounded-full animate-ping opacity-30" />
          </div>
        </AdvancedMarker>
      )}

      {/* Professional Markers */}
      {professionals.map((pro) => {
        const config = professionConfig[pro.profession_type] || professionConfig.veterinario;
        const Icon = config.icon;
        
        return (
          <AdvancedMarker
            key={pro.professional_id}
            position={{ lat: pro.lat, lng: pro.lng }}
            onClick={() => onSelect(pro)}
          >
            <div 
              className="cursor-pointer transform hover:scale-110 transition-transform"
              style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))' }}
            >
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center"
                style={{ backgroundColor: config.markerColor }}
              >
                <Icon size={20} className="text-white" />
              </div>
              <div 
                className="w-3 h-3 rotate-45 mx-auto -mt-1.5"
                style={{ backgroundColor: config.markerColor }}
              />
            </div>
          </AdvancedMarker>
        );
      })}
    </>
  );
}

// Map Placeholder (when API key not configured)
function MapPlaceholder({ onCenterUser, filteredCount, isMobile }) {
  return (
    <div className="h-full bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0">
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-blue-200/50 rounded-full blur-2xl" />
        <div className="absolute bottom-1/3 right-1/3 w-40 h-40 bg-indigo-200/50 rounded-full blur-2xl" />
        <div className="absolute top-1/2 right-1/4 w-24 h-24 bg-purple-200/50 rounded-full blur-2xl" />
      </div>

      {/* Fake map grid */}
      <div className="absolute inset-0 opacity-10">
        <div className="h-full w-full" style={{
          backgroundImage: `
            linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '50px 50px'
        }} />
      </div>

      {/* Sample markers */}
      <div className="absolute top-1/4 left-1/3">
        <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center shadow-lg">
          <Stethoscope size={16} className="text-white" />
        </div>
      </div>
      <div className="absolute top-1/2 right-1/3">
        <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center shadow-lg">
          <Dog size={16} className="text-white" />
        </div>
      </div>
      <div className="absolute bottom-1/3 left-1/2">
        <div className="w-8 h-8 rounded-full bg-pink-500 flex items-center justify-center shadow-lg">
          <Scissors size={16} className="text-white" />
        </div>
      </div>

      {/* Content */}
      <div className={`relative text-center ${isMobile ? 'px-6' : 'px-12'} max-w-md`}>
        <div className="w-16 h-16 bg-white rounded-2xl shadow-xl mx-auto mb-4 flex items-center justify-center">
          <MapIcon size={32} className="text-[#FF6B4A]" />
        </div>
        <h3 className="text-xl font-bold text-[#1D1D1F] mb-2">
          Mappa in arrivo!
        </h3>
        <p className="text-[#6E6E73] mb-4 text-sm">
          Configura la Google Maps API Key per visualizzare la mappa interattiva con tutti i {filteredCount} servizi nella tua zona.
        </p>
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
          <div className="flex items-start gap-2">
            <AlertCircle size={18} className="text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-amber-800 text-left">
              <strong>Per lo sviluppatore:</strong> Aggiungi REACT_APP_GOOGLE_MAPS_API_KEY nel file .env del frontend
            </p>
          </div>
        </div>
        <Button onClick={onCenterUser} className="bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl">
          <Navigation size={16} className="mr-2" />
          Usa la mia posizione
        </Button>
      </div>
    </div>
  );
}

// Mobile Map Placeholder - Interactive with draggable markers
function MapPlaceholderMobile({ onCenterUser, filteredCount, professionals, onSelectPro }) {
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [startPos, setStartPos] = useState({ x: 0, y: 0 });

  const handleTouchStart = (e) => {
    setIsDragging(true);
    setStartPos({ x: e.touches[0].clientX - dragOffset.x, y: e.touches[0].clientY - dragOffset.y });
  };

  const handleTouchMove = (e) => {
    if (!isDragging) return;
    setDragOffset({
      x: e.touches[0].clientX - startPos.x,
      y: e.touches[0].clientY - startPos.y
    });
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  return (
    <div 
      className="h-full bg-gradient-to-br from-blue-50 to-indigo-100 relative overflow-hidden touch-none"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* Draggable content */}
      <div 
        className="absolute inset-0 transition-transform"
        style={{ transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}
      >
        {/* Grid background */}
        <div className="absolute inset-[-100%] opacity-20" style={{
          backgroundImage: `
            linear-gradient(rgba(0,0,0,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(0,0,0,0.1) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px'
        }} />

        {/* Roads */}
        <div className="absolute top-1/2 left-0 right-0 h-6 bg-gray-300/50" />
        <div className="absolute left-1/3 top-0 bottom-0 w-4 bg-gray-300/40" />
        <div className="absolute right-1/4 top-0 bottom-0 w-6 bg-gray-300/50" />

        {/* Sample markers scattered */}
        {professionals.slice(0, 6).map((pro, i) => {
          const config = professionConfig[pro.profession_type] || professionConfig.veterinario;
          const Icon = config.icon;
          const positions = [
            { top: '20%', left: '25%' },
            { top: '35%', left: '60%' },
            { top: '55%', left: '30%' },
            { top: '45%', left: '75%' },
            { top: '70%', left: '50%' },
            { top: '25%', left: '80%' },
          ];
          return (
            <div 
              key={pro.professional_id}
              className="absolute cursor-pointer transform hover:scale-110 transition-transform"
              style={{ ...positions[i] }}
              onClick={() => onSelectPro(pro)}
            >
              <div 
                className="w-10 h-10 rounded-full flex items-center justify-center shadow-lg"
                style={{ backgroundColor: config.markerColor }}
              >
                <Icon size={18} className="text-white" />
              </div>
              <div 
                className="w-3 h-3 rotate-45 mx-auto -mt-1.5"
                style={{ backgroundColor: config.markerColor }}
              />
            </div>
          );
        })}
      </div>

      {/* Info Banner - Fixed */}
      <div className="absolute top-2 left-2 right-2 bg-white/95 backdrop-blur rounded-xl p-3 shadow-lg z-10">
        <div className="flex items-center gap-2">
          <AlertCircle size={16} className="text-amber-500 flex-shrink-0" />
          <p className="text-xs text-[#6E6E73]">
            <strong className="text-[#1D1D1F]">Demo:</strong> Trascina per esplorare • Tocca i marker per dettagli
          </p>
        </div>
      </div>

      {/* Bottom cards - Grid instead of carousel */}
      {professionals.length > 0 && (
        <div className="absolute bottom-16 left-0 right-0 px-3 z-10">
          <div className="grid grid-cols-2 gap-2 pb-2">
            {professionals.slice(0, 4).map((pro) => {
              const config = professionConfig[pro.profession_type] || professionConfig.veterinario;
              const Icon = config.icon;
              return (
                <div
                  key={pro.professional_id}
                  onClick={() => onSelectPro(pro)}
                  className="bg-white rounded-xl p-2.5 shadow-lg border border-black/5"
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-9 h-9 rounded-lg ${config.bg} flex items-center justify-center flex-shrink-0`}>
                      <Icon size={16} className={config.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-[#1D1D1F] text-xs truncate">{pro.name}</p>
                      <div className="flex items-center gap-1">
                        <Star size={10} className="fill-amber-400 text-amber-400" />
                        <span className="text-[10px] text-[#86868B]">{pro.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Center button */}
      <Button
        onClick={onCenterUser}
        className="absolute bottom-4 right-4 w-10 h-10 rounded-full bg-white shadow-lg z-10"
        variant="ghost"
      >
        <Navigation size={16} className="text-[#FF6B4A]" />
      </Button>
    </div>
  );
}

// Desktop List View
function ListViewDesktop({ professionals, onSelect, renderStars }) {
  if (professionals.length === 0) {
    return (
      <div className="text-center py-12">
        <Search className="mx-auto text-[#D1D1D6] mb-4" size={48} />
        <h3 className="font-semibold text-[#1D1D1F] mb-2">Nessun risultato</h3>
        <p className="text-[#86868B]">Prova a modificare i filtri di ricerca</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 gap-4">
      {professionals.map((pro) => {
        const config = professionConfig[pro.profession_type] || professionConfig.veterinario;
        const Icon = config.icon;
        
        return (
          <div
            key={pro.professional_id}
            className="bg-white rounded-2xl p-5 hover:shadow-lg transition-all cursor-pointer group border border-black/5"
            onClick={() => onSelect(pro)}
          >
            <div className="flex items-start gap-4">
              <div className={`w-14 h-14 rounded-xl ${config.bg} flex items-center justify-center flex-shrink-0`}>
                <Icon size={28} className={config.color} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between mb-1">
                  <h3 className="font-bold text-[#1D1D1F]">{pro.name}</h3>
                  {pro.verified && (
                    <CheckCircle className="text-emerald-500 flex-shrink-0" size={18} />
                  )}
                </div>
                
                <div className="flex items-center gap-2 mb-2">
                  <div className="flex">{renderStars(pro.rating)}</div>
                  <span className="text-sm text-[#86868B]">({pro.reviews_count})</span>
                </div>
                
                <p className="text-sm text-[#6E6E73] line-clamp-2 mb-2">{pro.description}</p>
                
                <div className="flex items-center gap-1 text-sm text-[#86868B]">
                  <MapPin size={14} />
                  <span>{pro.city}</span>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Professional Detail Dialog
function ProfessionalDialog({ professional, open, onClose, onBook, renderStars }) {
  if (!professional) return null;
  
  const config = professionConfig[professional.profession_type] || professionConfig.veterinario;
  const Icon = config.icon;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-[480px] rounded-3xl bg-white p-0 border-0 max-h-[85vh] overflow-hidden">
        {/* Header with icon - matching rounded corners */}
        <div className={`${config.bg} p-8 text-center relative rounded-t-3xl`}>
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/80 flex items-center justify-center hover:bg-white transition-colors"
          >
            <X size={18} className="text-[#6E6E73]" />
          </button>
          <div className="w-20 h-20 rounded-2xl bg-white shadow-lg mx-auto mb-4 flex items-center justify-center">
            <Icon size={40} className={config.color} />
          </div>
          <h2 className="text-xl font-bold text-[#1D1D1F]">{professional.name}</h2>
          <div className="flex items-center justify-center gap-2 mt-2">
            <div className="flex">{renderStars(professional.rating)}</div>
            <span className="text-[#6E6E73] text-sm">({professional.reviews_count} recensioni)</span>
          </div>
          {professional.verified && (
            <div className="inline-flex items-center gap-1 mt-3 bg-emerald-100 text-emerald-700 px-3 py-1 rounded-full text-sm font-medium">
              <CheckCircle size={14} />
              Verificato
            </div>
          )}
        </div>

        <div className="p-6 overflow-y-auto max-h-[50vh]">
          {/* Multi-roles badges */}
          {professional.profession_types && professional.profession_types.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {professional.profession_types.map((type, i) => {
                const typeConfig = professionConfig[type] || professionConfig.veterinario;
                const TypeIcon = typeConfig.icon;
                return (
                  <span key={i} className={`inline-flex items-center gap-1 ${typeConfig.bg} ${typeConfig.color} px-2 py-1 rounded-lg text-xs font-medium`}>
                    <TypeIcon size={12} />
                    {type.replace('_', ' ')}
                  </span>
                );
              })}
            </div>
          )}

          {/* Description */}
          <p className="text-[#6E6E73] mb-6">{professional.description}</p>

          {/* Info Cards */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-[#F5F5F7] rounded-xl p-4">
              <MapPin className="text-[#FF6B4A] mb-2" size={20} />
              <p className="text-sm text-[#86868B]">Località</p>
              <p className="font-semibold text-[#1D1D1F]">{professional.city}</p>
            </div>
            <div className="bg-[#F5F5F7] rounded-xl p-4">
              <Award className="text-[#FF6B4A] mb-2" size={20} />
              <p className="text-sm text-[#86868B]">Esperienza</p>
              <p className="font-semibold text-[#1D1D1F]">{professional.experience || "5+"} anni</p>
            </div>
          </div>

          {/* Services */}
          {professional.services && professional.services.length > 0 && (
            <div className="mb-6">
              <h3 className="font-semibold text-[#1D1D1F] mb-3">Servizi offerti</h3>
              <div className="flex flex-wrap gap-2">
                {professional.services.map((service, i) => (
                  <span key={i} className="bg-[#F5F5F7] text-[#6E6E73] px-3 py-1.5 rounded-lg text-sm">
                    {service}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Contact */}
          <div className="space-y-2 mb-6">
            {professional.phone && (
              <a href={`tel:${professional.phone}`} className="flex items-center gap-3 p-3 bg-[#F5F5F7] rounded-xl hover:bg-[#EBEBEB] transition-colors">
                <Phone className="text-[#6E6E73]" size={18} />
                <span className="text-[#1D1D1F]">{professional.phone}</span>
              </a>
            )}
            {professional.email && (
              <a href={`mailto:${professional.email}`} className="flex items-center gap-3 p-3 bg-[#F5F5F7] rounded-xl hover:bg-[#EBEBEB] transition-colors">
                <Mail className="text-[#6E6E73]" size={18} />
                <span className="text-[#1D1D1F]">{professional.email}</span>
              </a>
            )}
          </div>

          {/* Book Button */}
          <Button 
            onClick={onBook}
            className="w-full h-12 bg-[#FF6B4A] hover:bg-[#E05A3A] text-white rounded-xl font-semibold"
          >
            <Calendar className="mr-2" size={18} />
            Prenota ora
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
